import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { getProviderById, trackProviderContact, getProviderReviews } from '@/services/providerService';
import { useProviderContact } from '@/hooks/useProviderContact';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Star, MapPin, Phone, Mail, MessageCircle, Clock, CheckCircle, ArrowLeft, Lock, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ProviderScheduleCard from '@/components/ProviderScheduleCard';
import { useSubscription } from '@/hooks/useSubscription';
import { useTrialStatus } from '@/hooks/useTrialStatus';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { ProviderGallery } from '@/components/provider/ProviderGallery';
import { ProviderReviewForm } from '@/components/provider/ProviderReviewForm';

export default function ProviderProfile() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { subscription } = useSubscription();
  const { isInTrial } = useTrialStatus();
  const { fetchContactInfo, contactInfo, isLoading: isLoadingContact } = useProviderContact();
  const [contactFetched, setContactFetched] = useState(false);
  
  const canContact = subscription?.data?.subscription_tier !== 'free' || isInTrial;

  const { data: provider, isLoading } = useQuery({
    queryKey: ['provider', id],
    queryFn: () => getProviderById(id!),
    enabled: !!id,
  });

  const { data: reviews } = useQuery({
    queryKey: ['provider-reviews', id],
    queryFn: () => getProviderReviews(id!),
    enabled: !!id,
  });

  const handleContact = async (type: 'phone_view' | 'whatsapp_click' | 'email_click' | 'map_view', action: (contact?: typeof contactInfo) => void) => {
    if (!canContact) {
      navigate('/subscription');
      return;
    }

    // For contact actions that need PII, fetch securely first
    if (type !== 'map_view' && !contactFetched) {
      const contact = await fetchContactInfo(id!);
      if (!contact) {
        toast.error(t('services.contactError') || 'Could not fetch contact information');
        return;
      }
      setContactFetched(true);
      if (id) {
        await trackProviderContact(id, type);
      }
      action(contact);
    } else {
      if (id) {
        await trackProviderContact(id, type);
      }
      action(contactInfo || undefined);
    }
  };

  const handleMapView = async () => {
    if (!canContact) {
      navigate('/subscription');
      return;
    }
    if (id) {
      await trackProviderContact(id, 'map_view');
    }
    // Use public latitude/longitude for map (no PII)
    if (provider) {
      window.open(`https://maps.google.com/?q=${provider.latitude},${provider.longitude}`);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="container mx-auto px-4 py-8 text-center">
        <p className="text-muted-foreground">{t('services.providerNotFound')}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-muted/30">
      <div className="bg-card border-b">
        <div className="container mx-auto px-4 py-4">
          <Button variant="ghost" onClick={() => navigate(-1)}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            {t('common.back')}
          </Button>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 space-y-6">
            <Card className="p-6 bg-card">
              <div className="flex items-start gap-4 mb-6">
                {provider.photos && provider.photos.length > 0 && (
                  <img 
                    src={provider.photos[0]} 
                    alt={provider.business_name}
                    className="w-32 h-32 rounded-lg object-cover"
                  />
                )}
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <h1 className="text-3xl font-bold text-foreground">{provider.business_name}</h1>
                    {provider.verified && (
                      <Badge className="bg-primary/10 text-primary border-primary/20">
                        <CheckCircle className="w-3 h-3 mr-1" />
                        {t('services.verified')}
                      </Badge>
                    )}
                  </div>
                  
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex items-center">
                      <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                      <span className="ml-1 font-bold text-foreground">{provider.rating.toFixed(1)}</span>
                      <span className="ml-1 text-muted-foreground">({provider.total_reviews})</span>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-muted-foreground">
                    <MapPin className="w-4 h-4" />
                    <span>{provider.city}{provider.state ? `, ${provider.state}` : ''}{provider.country ? `, ${provider.country}` : ''}</span>
                  </div>
                </div>
              </div>

              {provider.description && (
                <div className="mb-6">
                  <h3 className="font-semibold mb-2 text-foreground">{t('services.about')}</h3>
                  <p className="text-muted-foreground">{provider.description}</p>
                </div>
              )}

              {provider.services && provider.services.length > 0 && (
                <div className="mb-6">
                  <h3 className="font-semibold mb-2 text-foreground">{t('services.servicesOffered')}</h3>
                  <div className="flex flex-wrap gap-2">
                    {provider.services.map((service, idx) => (
                      <Badge key={idx} variant="secondary">{service}</Badge>
                    ))}
                  </div>
                </div>
              )}
            </Card>

            {/* Gallery / Portfolio */}
            <ProviderGallery
              providerId={provider.id}
              userId={provider.user_id}
              isOwner={false}
            />

            {/* Review Form */}
            <ProviderReviewForm
              providerId={provider.id}
              providerUserId={provider.user_id}
            />

            {reviews && reviews.length > 0 && (
              <Card className="p-6 bg-card">
                <h3 className="font-semibold mb-4 text-foreground">{t('services.reviews')}</h3>
                <div className="space-y-4">
                  {reviews.map((review: any) => (
                    <div key={review.id} className="border-b border-border pb-4 last:border-0">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="flex">
                          {[...Array(5)].map((_, i) => (
                            <Star 
                              key={i}
                              className={`w-4 h-4 ${i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'text-muted'}`}
                            />
                          ))}
                        </div>
                        <span className="font-medium text-foreground">{review.users?.display_name}</span>
                      </div>
                      {review.comment && <p className="text-muted-foreground">{review.comment}</p>}
                    </div>
                  ))}
                </div>
              </Card>
            )}
          </div>

          <div className="lg:col-span-1 space-y-6">
            <Card className="p-6 bg-card">
              <h3 className="font-semibold mb-4 text-foreground">{t('services.contact')}</h3>
              {!canContact && (
                <Alert className="mb-4 border-amber-200 bg-amber-50">
                  <Lock className="h-4 w-4 text-amber-600" />
                  <AlertDescription className="text-amber-900">
                    <span className="font-medium">🔒 Upgrade</span> to contact providers
                  </AlertDescription>
                </Alert>
              )}
              <div className="space-y-3">
                <Button 
                  className="w-full" 
                  disabled={!canContact || isLoadingContact}
                  onClick={() => handleContact('phone_view', (contact) => {
                    if (contact?.phone) window.open(`tel:${contact.phone}`);
                  })}
                >
                  {isLoadingContact ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : !canContact ? (
                    <Lock className="w-4 h-4 mr-2" />
                  ) : (
                    <Phone className="w-4 h-4 mr-2" />
                  )}
                  {canContact ? t('services.callNow') : 'Upgrade to Call'}
                </Button>

                <Button 
                  className="w-full" 
                  variant="outline"
                  disabled={!canContact || isLoadingContact}
                  onClick={() => handleContact('whatsapp_click', (contact) => {
                    if (contact?.whatsapp) window.open(`https://wa.me/${contact.whatsapp}`);
                  })}
                >
                  {isLoadingContact ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : !canContact ? (
                    <Lock className="w-4 h-4 mr-2" />
                  ) : (
                    <MessageCircle className="w-4 h-4 mr-2" />
                  )}
                  {canContact ? 'WhatsApp' : 'Upgrade for WhatsApp'}
                </Button>

                <Button 
                  className="w-full" 
                  variant="outline"
                  disabled={!canContact || isLoadingContact}
                  onClick={() => handleContact('email_click', (contact) => {
                    if (contact?.email) window.open(`mailto:${contact.email}`);
                  })}
                >
                  {isLoadingContact ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : !canContact ? (
                    <Lock className="w-4 h-4 mr-2" />
                  ) : (
                    <Mail className="w-4 h-4 mr-2" />
                  )}
                  {canContact ? t('services.sendEmail') : 'Upgrade to Email'}
                </Button>

                <Button 
                  className="w-full" 
                  variant="outline"
                  disabled={!canContact}
                  onClick={handleMapView}
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  {t('services.viewOnMap')}
                </Button>
              </div>
            </Card>

            {provider.schedule && (
              <ProviderScheduleCard schedule={provider.schedule} />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
